import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { Box, Switch, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  getMemberAppLinkAutoApprove,
  previewMemberAppLinkAutoApprove,
  processMemberAppLinkAutoApprove,
  setMemberAppLinkAutoApprove,
} from '../../api/memberAppLink';
import CommonModalLayout from '../../form-components/CommonModalLayout';
import CommonModalButton from '../../form-components/CommonModalButton';
import CommonLoading from '../../form-components/CommonLoading';
import { CompareHeading, PreviousLinkInfo, ResultItem, ResultList, ResultSub } from './MemberAppLinkStyled';

// 켜기 흐름 단계 — 오매칭 고지 → (대기 요청이 있으면) 미리보기 → 처리 결과
const STEP_NOTICE = 'notice';
const STEP_PREVIEW = 'preview';
const STEP_RESULT = 'result';

const TEMPORARY_USER_TYPE = 'T';

// 확인 필요 사유(서버 result) → 직원 문구
const reasonText = item => {
  switch (item.result) {
    case 'UNVERIFIED':
      return '앱 휴대폰 미인증';
    case 'NO_MATCH':
      return '같은 번호 없음';
    case 'MULTIPLE':
      return `같은 번호 ${item.candidateCount}명`;
    case 'BLOCKED':
      return item.message || '합치기 불가';
    case 'NOT_APPLICABLE':
      return '이미 처리됨';
    case 'DISABLED':
      return '자동 승인 꺼짐';
    default:
      return item.result;
  }
};

const targetText = target =>
  target ? `${target.name} (${target.userType === TEMPORARY_USER_TYPE ? '잠재회원' : '회원'})` : '';

const accountText = account => `${account?.name ?? ''} (${account?.username ?? ''})`;

const ToggleBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '0 4px 0 12px',
  height: '40px',
  border: '1px solid #E5E5E5',
  borderRadius: '8px',
  background: '#fff',
});

const ToggleLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
});

const TealSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
});

const NoticeList = styled('ul')({
  margin: '8px 0 0',
  paddingLeft: '18px',
  fontSize: '14px',
  color: '#262626',
  lineHeight: 1.7,
});

const SummaryText = styled(Typography)({
  fontSize: '15px',
  color: '#262626',
  lineHeight: 1.6,
  marginBottom: '16px',
});

const Section = styled(Box)({
  marginTop: '16px',
});

/** 결과 목록 — 승인(가능) 건은 연결 대상, 확인 필요 건은 사유를 오른쪽에 둔다. */
const ItemList = ({ items, showTarget }) => (
  <ResultList>
    {items.map(item => (
      <ResultItem key={item.linkId} as="div" sx={{ cursor: 'default', '&:hover': { borderColor: '#E5E5E5', background: '#fff' } }}>
        <span>
          {accountText(item.appAccount)} <ResultSub>{item.appAccount?.phoneNumber || '-'}</ResultSub>
        </span>
        <ResultSub>{showTarget ? targetText(item.target) : reasonText(item)}</ResultSub>
      </ResultItem>
    ))}
  </ResultList>
);

/**
 * 앱 연동 요청 자동 승인 스위치(#195). 켤 때 오매칭 가능 고지를 확인받고, 기존 대기 요청이 있으면 처리 여부를 묻는다.
 * 끌 때는 바로 끈다(이미 연동된 건은 그대로).
 */
const AutoApproveControl = ({ branchId }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(null);
  const [report, setReport] = useState(null);

  const { data: setting } = useQuery({
    queryKey: ['memberAppLinkAutoApprove', branchId],
    queryFn: () => getMemberAppLinkAutoApprove(branchId),
    enabled: !!branchId,
  });
  const enabled = !!setting?.enabled;

  const refreshLists = () => {
    queryClient.invalidateQueries({ queryKey: ['memberAppLinkAutoApprove'] });
    queryClient.invalidateQueries({ queryKey: ['memberAppLinks'] });
    queryClient.invalidateQueries({ queryKey: ['memberAppLinkPendingCount'] });
  };

  const previewQuery = useQuery({
    queryKey: ['memberAppLinkAutoApprovePreview', branchId],
    queryFn: () => previewMemberAppLinkAutoApprove(branchId),
    enabled: step === STEP_PREVIEW,
    gcTime: 0,
  });

  const toggleMutation = useMutation({
    mutationFn: nextEnabled => setMemberAppLinkAutoApprove(branchId, nextEnabled),
    onSuccess: result => {
      refreshLists();
      if (!result?.enabled) {
        toast.success('자동 승인을 껐습니다.');
        return;
      }
      toast.success('자동 승인을 켰습니다.');
      if (result.pendingCount > 0) {
        setStep(STEP_PREVIEW);
      } else {
        setStep(null);
      }
    },
  });

  const processMutation = useMutation({
    mutationFn: () => processMemberAppLinkAutoApprove(branchId),
    onSuccess: result => {
      setReport(result);
      setStep(STEP_RESULT);
      refreshLists();
      queryClient.invalidateQueries({ queryKey: ['memberAppLinkHistory'] });
      queryClient.invalidateQueries({ queryKey: ['temporaryUsers'] });
    },
  });

  // 더블클릭이 리렌더 전에 두 번 들어오는 것을 막는다
  const isProcessing = useRef(false);
  const runProcess = () => {
    if (isProcessing.current) return;
    isProcessing.current = true;
    processMutation.mutate(undefined, { onSettled: () => (isProcessing.current = false) });
  };

  const handleSwitch = event => {
    if (event.target.checked) {
      setStep(STEP_NOTICE);
    } else {
      toggleMutation.mutate(false);
    }
  };

  const close = () => {
    setStep(null);
    setReport(null);
  };

  const preview = previewQuery.data;
  const approvableCount = preview?.approvable?.length ?? 0;

  const footer =
    step === STEP_NOTICE ? (
      <>
        <CommonModalButton variant="cancel" onClick={close} disabled={toggleMutation.isPending}>
          취소
        </CommonModalButton>
        <CommonModalButton onClick={() => toggleMutation.mutate(true)} disabled={toggleMutation.isPending}>
          확인하고 켜기
        </CommonModalButton>
      </>
    ) : step === STEP_PREVIEW ? (
      <>
        <CommonModalButton variant="cancel" onClick={close} disabled={processMutation.isPending}>
          {approvableCount > 0 ? '나중에' : '닫기'}
        </CommonModalButton>
        {approvableCount > 0 && (
          <CommonModalButton onClick={runProcess} disabled={processMutation.isPending || previewQuery.isFetching}>
            {approvableCount}건 자동 승인
          </CommonModalButton>
        )}
      </>
    ) : (
      <CommonModalButton onClick={close}>닫기</CommonModalButton>
    );

  return (
    <>
      <ToggleBox>
        <ToggleLabel>자동 승인</ToggleLabel>
        <TealSwitch
          checked={enabled}
          onChange={handleSwitch}
          disabled={!setting || toggleMutation.isPending}
          inputProps={{ 'aria-label': '앱 연동 요청 자동 승인' }}
        />
      </ToggleBox>

      <CommonModalLayout
        open={!!step}
        onClose={close}
        title={step === STEP_NOTICE ? '자동 승인 켜기' : step === STEP_PREVIEW ? '대기 중인 요청 처리' : '자동 승인 결과'}
        width={600}
        footer={footer}>
        {step === STEP_NOTICE && (
          <>
            <SummaryText>
              회원이 앱에서 연동을 요청하거나 센터 QR을 찍으면, 아래 조건이 모두 맞을 때 직원 승인 없이 바로 연동합니다.
            </SummaryText>
            <NoticeList>
              <li>
                <b>회원이 앱에서 휴대폰 번호 인증을 마쳤을 때만</b> 자동 매칭됩니다. 인증하지 않은 회원은 자동 승인되지 않고 대기로
                남습니다(카카오로 가입한 회원은 가입할 때 인증됨).
              </li>
              <li>이 지점에 같은 휴대폰 번호로 등록된 회원·잠재회원이 딱 1명일 때</li>
              <li>조건이 맞지 않으면 지금처럼 대기로 남고 직원이 처리합니다.</li>
            </NoticeList>
            <SummaryText style={{ marginTop: '12px', marginBottom: 0, fontSize: '14px', color: '#525252' }}>
              자동 매칭을 받으려면 회원에게 앱 MY &gt; 회원정보수정 &gt; 휴대폰 인증을 먼저 하도록 안내해 주세요.
            </SummaryText>
            <PreviousLinkInfo style={{ marginTop: '16px', marginBottom: 0 }}>
              <b>주의</b>
              <NoticeList style={{ marginTop: '4px' }}>
                <li>자동 승인은 휴대폰 번호가 같은지만 보고 연결합니다.</li>
                <li>가족이 한 번호를 같이 쓰거나 센터에 번호가 잘못 등록돼 있으면 다른 사람과 연결될 수 있습니다.</li>
              </NoticeList>
            </PreviousLinkInfo>
          </>
        )}

        {step === STEP_PREVIEW &&
          (previewQuery.isFetching || !preview ? (
            <CommonLoading />
          ) : (
            <>
              <SummaryText>
                대기 중인 요청 {preview.total}건 중 자동 승인 가능 {approvableCount}건, 확인 필요{' '}
                {preview.needsReview.length}건입니다.
                {approvableCount > 0 ? ' 지금 자동 승인할까요?' : ' 확인 필요 건은 직원이 직접 처리해 주세요.'}
              </SummaryText>
              {approvableCount > 0 && (
                <Section>
                  <CompareHeading>자동 승인 가능 {approvableCount}건</CompareHeading>
                  <ItemList items={preview.approvable} showTarget />
                </Section>
              )}
              {preview.needsReview.length > 0 && (
                <Section>
                  <CompareHeading>확인 필요 {preview.needsReview.length}건 (대기로 남음)</CompareHeading>
                  <ItemList items={preview.needsReview} />
                </Section>
              )}
            </>
          ))}

        {step === STEP_RESULT && report && (
          <>
            <SummaryText>
              자동 승인 {report.approvable.length}건을 처리했습니다. 확인 필요 {report.needsReview.length}건은 대기 탭에서
              직접 처리해 주세요.
            </SummaryText>
            {report.approvable.length > 0 && (
              <Section>
                <CompareHeading>자동 승인됨 {report.approvable.length}건</CompareHeading>
                <ItemList items={report.approvable} showTarget />
              </Section>
            )}
            {report.needsReview.length > 0 && (
              <Section>
                <CompareHeading>확인 필요 {report.needsReview.length}건</CompareHeading>
                <ItemList items={report.needsReview} />
              </Section>
            )}
          </>
        )}
      </CommonModalLayout>
    </>
  );
};

export default AutoApproveControl;
